<template>
  <div class="py-4">
    <!-- 안내 -->
    <v-card class="mx-auto mb-4" max-width="1150">
      <v-toolbar color="deep-purple darken-1" dark dense flat>
        <v-toolbar-title>애플 계정복구 (CS) — APPLE AUTH_ID 스왑</v-toolbar-title>
      </v-toolbar>
      <v-card-text class="pb-2">
        <v-alert dense type="warning" text class="mb-0">
          유저가 지금 로그인하는 <b>신(빈) 계정</b>의 애플 인증을 <b>구(데이터) 계정</b>에 스왑합니다.
          <b>잘못 매칭하면 남의 계정이 넘어갑니다.</b> 전화번호·닉네임·유저ID로 먼저 확인하고, 확실할 때만 실행하세요.
          (IP 매칭은 참고용 후보일 뿐입니다.)
        </v-alert>
      </v-card-text>
    </v-card>

    <!-- ① 계정 검색 -->
    <v-card class="mx-auto mb-4" max-width="1150">
      <v-card-title class="subtitle-1 font-weight-bold">① 계정 검색 (전화번호 / 닉네임 / 유저ID)</v-card-title>
      <v-card-text>
        <v-row dense align="center">
          <v-col cols="12" md="3"><v-text-field v-model="search.phone" label="전화번호" dense outlined hide-details clearable @keyup.enter="onSearch" /></v-col>
          <v-col cols="12" md="3"><v-text-field v-model="search.nick" label="닉네임" dense outlined hide-details clearable @keyup.enter="onSearch" /></v-col>
          <v-col cols="12" md="3"><v-text-field v-model="search.user_id" label="유저ID" dense outlined hide-details clearable @keyup.enter="onSearch" /></v-col>
          <v-col cols="12" md="3"><v-btn color="primary" block :loading="loading.search" @click="onSearch"><v-icon left>mdi-magnify</v-icon>검색</v-btn></v-col>
        </v-row>
        <v-data-table :headers="candHeaders" :items="candidates" dense class="mt-3" :items-per-page="10" no-data-text="검색 결과 없음">
          <template v-slot:[`item.GEM`]="{ item }">{{ fmt(item.GEM) }}</template>
          <template v-slot:[`item.action`]="{ item }">
            <v-btn x-small color="teal" dark class="mr-1" @click="pickOld(item.USER_ID)">구계정</v-btn>
            <v-btn x-small color="orange darken-1" dark @click="pickNew(item.USER_ID)">신계정</v-btn>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- ② IP 매칭 도우미 -->
    <v-card class="mx-auto mb-4" max-width="1150">
      <v-card-title class="subtitle-1 font-weight-bold">② IP 매칭 도우미 <span class="caption grey--text ml-2">신 계정과 같은 IP를 쓴 구 계정 후보 (참고용)</span></v-card-title>
      <v-card-text>
        <v-row dense align="center">
          <v-col cols="12" md="4"><v-text-field v-model="ipMatch.user_id" label="신(대상) 유저ID" dense outlined hide-details clearable /></v-col>
          <v-col cols="12" md="3"><v-btn color="indigo" dark block :loading="loading.match" @click="onMatchIp"><v-icon left>mdi-account-search</v-icon>IP 매칭 후보</v-btn></v-col>
          <v-col cols="12" md="3"><v-btn color="blue-grey" dark block :loading="loading.ip" @click="onUserIps"><v-icon left>mdi-ip-network</v-icon>IP 목록</v-btn></v-col>
        </v-row>
        <div v-if="targetIps.length" class="caption mt-2">대상 IP({{ targetIps.length }}): {{ targetIps.join(', ') }}</div>

        <v-data-table v-if="matchCandidates.length" :headers="matchHeaders" :items="matchCandidates" dense class="mt-2" :items-per-page="10">
          <template v-slot:[`item.GEM`]="{ item }">{{ fmt(item.GEM) }}</template>
          <template v-slot:[`item.action`]="{ item }">
            <v-btn x-small color="teal" dark @click="pickOld(item.USER_ID)">구계정 지정</v-btn>
          </template>
        </v-data-table>

        <v-data-table v-if="ipList.length" :headers="ipHeaders" :items="ipList" dense class="mt-2" :items-per-page="5" />
      </v-card-text>
    </v-card>

    <!-- ③ 스왑 실행 — 실제 계정 교체(핵심). 위험 강조 -->
    <v-card class="mx-auto mb-4 auth-swap-card" max-width="1150" color="grey lighten-4" elevation="10">
      <v-toolbar color="red darken-2" dark dense flat>
        <v-icon left>mdi-swap-horizontal-bold</v-icon>
        <v-toolbar-title class="font-weight-bold">③ 스왑 실행 — 실제 계정 교체 (핵심)</v-toolbar-title>
      </v-toolbar>
      <v-card-text class="pt-4">
        <!-- 선택된 두 계정을 크게 -->
        <v-row align="center" justify="center" dense>
          <v-col cols="12" md="5">
            <v-card outlined class="pa-3 acc-box acc-old">
              <div class="overline teal--text text--darken-2 font-weight-bold mb-1">
                <v-icon small color="teal darken-2">mdi-account-check</v-icon> 구(데이터) 계정 · 되찾을 계정
              </div>
              <v-text-field v-model="swap.user_id_old" placeholder="위에서 '구계정' 클릭 또는 직접 입력"
                solo flat hide-details dense background-color="teal lighten-4"
                class="acc-input teal--text text--darken-4" clearable />
            </v-card>
          </v-col>
          <v-col cols="12" md="2" class="text-center py-0">
            <v-icon size="44" color="red darken-2">mdi-swap-horizontal-bold</v-icon>
            <div class="caption red--text text--darken-2 font-weight-bold">SWAP</div>
          </v-col>
          <v-col cols="12" md="5">
            <v-card outlined class="pa-3 acc-box acc-new">
              <div class="overline orange--text text--darken-3 font-weight-bold mb-1">
                <v-icon small color="orange darken-3">mdi-account-alert</v-icon> 신(빈) 계정 · 지금 로그인된 계정
              </div>
              <v-text-field v-model="swap.user_id_new" placeholder="위에서 '신계정' 클릭 또는 직접 입력"
                solo flat hide-details dense background-color="orange lighten-4"
                class="acc-input orange--text text--darken-4" clearable />
            </v-card>
          </v-col>
        </v-row>

        <v-row dense class="mt-3" align="center">
          <v-col cols="12" md="8">
            <v-text-field v-model="swap.reason" label="사유 (권장)" outlined dense hide-details
              background-color="white" prepend-inner-icon="mdi-note-text-outline" />
          </v-col>
          <v-col cols="12" md="4">
            <v-btn color="red darken-2" dark block x-large elevation="4" class="swap-exec-btn"
              :disabled="!swap.user_id_old || !swap.user_id_new" @click="confirmDialog = true">
              <v-icon left>mdi-swap-horizontal-bold</v-icon> AUTH_ID 스왑 실행
            </v-btn>
            <div v-if="!swap.user_id_old || !swap.user_id_new" class="caption red--text text--darken-2 text-center mt-1">
              구·신 계정을 모두 지정하면 실행됩니다
            </div>
          </v-col>
        </v-row>

        <v-alert v-if="swapResult" type="success" text prominent class="mt-3 mb-0">{{ swapResult }}</v-alert>
      </v-card-text>
    </v-card>

    <!-- ④ 이력 -->
    <v-card class="mx-auto" max-width="1150">
      <v-card-title class="subtitle-1 font-weight-bold">
        ④ 교체 이력
        <v-spacer></v-spacer>
        <v-text-field v-model="logFilter" label="유저ID 필터" dense hide-details clearable style="max-width:220px" class="mr-2" @keyup.enter="onLoadLog" />
        <v-btn small @click="onLoadLog"><v-icon left small>mdi-refresh</v-icon>조회</v-btn>
      </v-card-title>
      <v-data-table :headers="logHeaders" :items="logs" dense :items-per-page="10" no-data-text="이력 없음" class="mt-1" />
    </v-card>

    <!-- 확인 다이얼로그 -->
    <v-dialog v-model="confirmDialog" max-width="560">
      <v-card>
        <v-card-title class="red--text">스왑 최종 확인</v-card-title>
        <v-card-text>
          <div class="mb-1"><b>구(되찾을) 계정:</b> <span class="teal--text">{{ swap.user_id_old }}</span></div>
          <div class="mb-1"><b>신(빈) 계정:</b> <span class="orange--text text--darken-2">{{ swap.user_id_new }}</span></div>
          <div v-if="swap.reason" class="mb-1"><b>사유:</b> {{ swap.reason }}</div>
          <v-divider class="my-2"></v-divider>
          <div class="caption">실행 후 유저가 애플 로그인하면 <b>{{ swap.user_id_old }}</b> 로 진입합니다.
            되돌리려면 두 계정을 반대로 다시 스왑해야 합니다. 계정이 맞는지 다시 확인하세요.</div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="confirmDialog = false">취소</v-btn>
          <v-btn color="red darken-2" dark :loading="loading.apply" @click="onApply">스왑 실행</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions } from 'vuex';

export default {
  name: 'CarryGold_Auth_Recover',

  data: () => ({
    loading: { search: false, match: false, ip: false, apply: false },

    search: { phone: '', nick: '', user_id: '' },
    candidates: [],
    candHeaders: [
      { text: '유저ID', value: 'USER_ID' },
      { text: '닉네임', value: 'NICK' },
      { text: '전화번호', value: 'PHONE' },
      { text: 'AUTH_ID', value: 'AUTH_ID' },
      { text: '보석', value: 'GEM' },
      { text: '최근로그인', value: 'LAST_LOGIN_DT' },
      { text: '지정', value: 'action', sortable: false },
    ],

    ipMatch: { user_id: '' },
    targetIps: [],
    matchCandidates: [],
    matchHeaders: [
      { text: '유저ID', value: 'USER_ID' },
      { text: '공유IP수', value: 'SHARED_IP_CNT' },
      { text: '닉네임', value: 'NICK' },
      { text: '전화번호', value: 'PHONE' },
      { text: '보석', value: 'GEM' },
      { text: '공유 IP', value: 'SHARED_IPS' },
      { text: '지정', value: 'action', sortable: false },
    ],
    ipList: [],
    ipHeaders: [
      { text: 'IP', value: 'IP' },
      { text: '로그인횟수', value: 'LOG_CNT' },
      { text: '최초', value: 'FIRST_DT' },
      { text: '최근', value: 'LAST_DT' },
    ],

    swap: { user_id_old: '', user_id_new: '', reason: '' },
    confirmDialog: false,
    swapResult: '',

    logFilter: '',
    logs: [],
    logHeaders: [
      { text: '시간', value: 'CREATE_DT' },
      { text: '유저ID', value: 'USER_ID' },
      { text: 'AUTH_ID_OLD', value: 'AUTH_ID_OLD' },
      { text: 'AUTH_ID_NEW', value: 'AUTH_ID_NEW' },
      { text: '결과', value: 'RESULT_CODE' },
      { text: 'IP', value: 'USER_IP' },
      { text: '비고', value: 'ETC_INFO' },
    ],
  }),

  created() {
    this.onLoadLog();
  },

  methods: {
    ...mapActions([
      'CARRYGOLD_AUTH_RECOVER_SEARCH',
      'CARRYGOLD_AUTH_RECOVER_USER_IPS',
      'CARRYGOLD_AUTH_RECOVER_MATCH_IP',
      'CARRYGOLD_AUTH_RECOVER_APPLY',
      'CARRYGOLD_AUTH_RECOVER_LOG',
    ]),

    // 백엔드 성공 응답은 response_data 필드가 최상위로 오거나 감싸여 올 수 있어 둘 다 처리
    unwrap(body) {
      if (body && body.response_data) return body.response_data;
      return body || {};
    },
    fmt(n) { return n == null ? '-' : Number(n).toLocaleString(); },

    async onSearch() {
      this.loading.search = true;
      try {
        const r = this.unwrap(await this.CARRYGOLD_AUTH_RECOVER_SEARCH({ ...this.search }));
        this.candidates = r.candidates || [];
      } catch (e) {
        alert('검색 실패: ' + (e.message || e));
      } finally { this.loading.search = false; }
    },

    pickOld(id) { this.swap.user_id_old = id; },
    pickNew(id) { this.swap.user_id_new = id; this.ipMatch.user_id = id; },

    async onMatchIp() {
      if (!this.ipMatch.user_id) return;
      this.loading.match = true;
      try {
        const r = this.unwrap(await this.CARRYGOLD_AUTH_RECOVER_MATCH_IP({ user_id: this.ipMatch.user_id }));
        this.targetIps = r.target_ips || [];
        this.matchCandidates = r.candidates || [];
        this.swap.user_id_new = this.ipMatch.user_id;
        if (this.matchCandidates.length === 0) {
          alert('IP 겹치는 후보가 없습니다. (스냅샷 미적재 or 겹침 없음)');
        }
      } catch (e) {
        alert('IP 매칭 실패: ' + (e.message || e));
      } finally { this.loading.match = false; }
    },

    async onUserIps() {
      if (!this.ipMatch.user_id) return;
      this.loading.ip = true;
      try {
        const r = this.unwrap(await this.CARRYGOLD_AUTH_RECOVER_USER_IPS({ user_id: this.ipMatch.user_id }));
        this.ipList = r.ip_list || [];
      } catch (e) {
        alert('IP 조회 실패: ' + (e.message || e));
      } finally { this.loading.ip = false; }
    },

    async onApply() {
      this.loading.apply = true;
      try {
        const r = this.unwrap(await this.CARRYGOLD_AUTH_RECOVER_APPLY({ ...this.swap }));
        if (r.err_message) { alert('스왑 실패: ' + r.err_message); return; }
        this.swapResult = r.message || '스왑 완료';
        this.confirmDialog = false;
        this.onLoadLog();
      } catch (e) {
        alert('스왑 실패: ' + (e.message || e));
      } finally { this.loading.apply = false; }
    },

    async onLoadLog() {
      try {
        const r = this.unwrap(await this.CARRYGOLD_AUTH_RECOVER_LOG({ user_id: this.logFilter }));
        this.logs = r.logs || [];
      } catch (e) { /* 이력 조회 실패는 조용히 */ }
    },
  },
};
</script>

<!-- 클래스명이 이 화면 전용이라 비-scoped 로 둬도 다른 화면에 영향 없음 (deep selector 회피) -->
<style>
.auth-swap-card { border: 2px solid #e53935 !important; }
.acc-box { border-radius: 10px; }
.acc-old { border: 2px solid #009688 !important; background-color: #e0f2f1 !important; }
.acc-new { border: 2px solid #fb8c00 !important; background-color: #fff3e0 !important; }
.acc-input input {
  font-size: 19px !important;
  font-weight: 700 !important;
  letter-spacing: .5px;
}
/* 비활성 상태(회색)에서도 버튼이 배경에 묻히지 않게 항상 빨간 테두리 */
.swap-exec-btn {
  border: 2px solid #b71c1c !important;
  min-height: 52px;
}
.swap-exec-btn.v-btn--disabled {
  opacity: 0.9;
  color: #b71c1c !important;
}
</style>
